/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */

import dayjs = require("dayjs");

import { CashflowResultWithLatestResult } from "../view_models/balancesheet_selectors";

/**
 * TODO: このobjectは一つのtypeとして定義し、
 * MontlyCashflowResultを流用しないこと。
 * また、この型定義の中に cumulative (累積)系も入れられるはず。
 *
 * https://github.com/Propally/api_schema/issues/96
 *
 * ----
 * 2024.02.24 追記
 * 上述コメントの「MontlyCashflowResultを流用してはいけない」背景が分からなかったため万が一何かあった際は適宜戻せるようにコメントを残す.
 *
 * 元々MonthlyCashflowResultWithMonth型(Open API schema定義型)であったが、以下対応時にMonthlyCashflowResult型に変更した.
 *   schema共通化対応: https://github.com/Propally/api_schema/pull/259/commits/4f5bd2be52b1929f931d90f3206802bf0ea36398
 * 主な内容としては元々month項目のなかったMonthlyCashflowResultスキーマを削除をし、新しく準備したmonth項目もあるMonthlyCashflowResultスキーマに差し替えたのみであり、
 * さらに実質MonthlyCashflowResultWithMonth型と同様となったため、MonthlyCashflowResultWithMonthは不要で問題ないと判断して削除した。
 * そのため正確には上述で指しているMontlyCashflowResultとは異なるものとなるが、万が一の場合の備忘としてコメントを残した.
 * ------
 */
export const MonthlyCashflowResultTemplate: CashflowResultWithLatestResult = {
  /**
   * @name month "計算対象月"
   * @summary
   */
  month: dayjs().format("YYYY/MM"),

  /**
   * @name rent_income "賃料収入"
   * @summary 対象物件の賃料の合算
   * @description `month` が契約開始月/終了月に該当する場合，日割り計算が行われる．
   */
  rent_income: 0,

  /**
   * @name management_fee_income "管理費収入"
   * @summary 対象物件の __現在の契約における__ 管理費の合算
   * @description `month` が契約開始月/終了月に該当する場合，日割り計算が行われる．
   */
  management_fee_income: 0,

  /**
   * @name management_cost "賃貸管理費"
   * @summary  対象物件の賃貸管理費（支出）の合算
   * @description `month` が購入月に該当する場合，日割り計算が行われる．
   */
  management_cost: 0,

  /**
   * @name building_management_cost "建物管理費"
   * @summary  対象物件の建物管理費（支出）の合算
   * @description `month` が購入月に該当する場合，日割り計算が行われる．
   */
  building_management_cost: 0,

  /**
   * @name repair_fund_cost "修繕積立金"
   * @summary  対象物件の修繕積立金（支出）の合算
   * @description `month` が購入月に該当する場合，日割り計算が行われる．
   */
  repair_fund_cost: 0,

  /**
   * @name misc_cost "その他経費"
   * @summary  対象物件のその他経費（支出）の合算
   */
  misc_cost: 0,

  /**
   * @name loan_interest "利息"
   * @summary  対象物件のローンの利息支払い額の合計
   * @see https://www.nomu.com/loan/story/05.html
   * @see https://gist.github.com/pies/4166888
   * @description 元利均等返済方式のみ採用．
   */
  loan_interest: 0,

  /**
   * @name loan_interest_rate "利率"
   * @summary  対象物件のローンの利息支払いの利率．
   */
  loan_interest_rate_percentage: 0,

  /**
   * @name loan_interest "元金"
   * @summary  対象物件のローンの元金支払い額の合計
   * @see https://www.nomu.com/loan/story/05.html
   * @see https://gist.github.com/pies/4166888
   * @description 元利均等返済と元金均等返済の２方式が存在する．
   * */
  loan_principal: 0,

  /**
   * @name loan_prepayment "繰上返済"
   * @summary  対象物件のローンの繰上げ返済金額の合計
   * */
  loan_prepayment: 0,

  /**
   * @name key_money "礼金"
   * @summary  対象物件の礼金の合計
   * @description 契約月のみの収入.更新料とどちらかのみが発生する（どちらかの値は常にゼロ).
   * */
  key_money: 0,

  /**
   * @name security_deposit "更新料"
   * @summary 対象物件の契約更新料の合計
   * @description 契約更新月のみの収入．礼金とどちらかのみが発生する（どちらかの値は常にゼロ).
   * */
  security_deposit: 0,

  /**
   * @name property_tax "固定資産税・都市計画税"
   * @summary 対象物件の固定資産税額の合計
   * @description
   *   X1年1月に支払い義務が発生する．
   *   固定資産税の分割払いを選択している場合，6,9,12, X2年2月の4回.
   *   ただし，購入年度は発生しない．
   * */
  property_tax: 0,

  /**
   * @name fire_insurance_cost "火災保険料"
   * @summary 対象物件の火災保険料の合計
   * @description
   *   前払い．契約初月に全額支払い．
   * @todo
   *   「横引き」する？
   * */
  fire_insurance_cost: 0,

  /**
   * @name earthquake_insurance_cost "地震保険料"
   * @summary 対象物件の地震保険料の合計
   * @description
   *   前払い．契約初月に全額支払い．
   * @todo
   *   「横引き」する？
   * */
  earthquake_insurance_cost: 0,

  /**
   * @name large_scale_repair_fund_cost "設備交換大規模修繕費用"
   * @summary 対象物件の設備交換・大規模修繕費用の合計
   * */
  large_scale_repair_fund_cost: 0,

  /**
   * @name misc_onetime_cost "その他一過性の支出"
   * @summary 対象物件のその他一過性の支出の合計
   * @description
   *   このフィールドは，自動計算されない．ユーザによる実績更新の入力によってのみ値が入力される．
   * */
  misc_onetime_cost: 0,

  /**
   * @name tax_on_real_estates "不動産所得にかかる税金"
   * @summary 対象物件の不動産所得にかかる税金の支出の合計
   * @description
   *   「確定申告用の不動産所得」と「ユーザの所得税率」の掛け算．
   * @todo 上記どちらも未実装．
   * */
  tax_on_real_estates: 0,

  /**
   * @name  "新契約業務委託料"
   * @summary 新規賃貸借契約締結時にかかる手数料（管理会社への支払い）
   * @description
   *   Propertyモデルに入っているのは月額賃料に対する係数．
   *   このフィールドに入れるのは実数．
   * */
  outsourcing_fee_of_new_lease_contract: 0,

  /**
   * @name  "更新業務委託料"
   * @summary 賃貸借契約更新時にかかる手数料（管理会社への支払い）
   * @description
   *   Propertyモデルに入っているのは月額賃料に対する係数．
   *   このフィールドに入れるのは実数．
   * */
  outsourcing_fee_of_updating_lease_contract: 0,

  fire_insurance_distributed_price: 0,
  earthquake_insurance_distributed_price: 0,
  monthly_building_depreciation: 0,
  monthly_facilities_depreciation: 0,
  real_estate_income_of_last_year: 0,
  real_estate_income_before_deduction_of_blue_returns: 0,
};
